import Box from '@mui/material/Box'
import type { SxProps, Theme } from '@mui/material/styles'

/*
 * Farm-fresh storefront motifs (redesign, Aug 2026). These are app-level design assets — the
 * same for every shop, not tenant data. ponytail: placeholder art the designer offered to
 * refine; swap the SVG / tune the divider anytime without touching callers.
 */

/** A little egg-chick mascot for the hero. Reads on a warm/terracotta panel (cream body, yolk beak). */
export function ChickMascot({ size = 88, sx }: { size?: number; sx?: SxProps<Theme> }) {
  return (
    <Box
      component="svg"
      viewBox="0 0 100 100"
      width={size}
      height={size}
      role="img"
      aria-hidden
      sx={sx}
    >
      {/* body (egg-shaped) */}
      <ellipse cx="50" cy="58" rx="34" ry="36" fill="#FFF3D6" />
      {/* wing */}
      <path d="M30 56c-8 4-10 14-4 20 5 5 12 4 15-2z" fill="#F4E2B4" />
      {/* head tuft */}
      <path d="M50 20c3-8 10-8 8 0-1 4-5 5-8 3z" fill="#F4C85A" />
      {/* beak */}
      <path d="M62 44l14 4-14 6z" fill="#E9A63C" />
      {/* eye */}
      <circle cx="58" cy="42" r="3.4" fill="#3B2A1E" />
      {/* cheek */}
      <circle cx="66" cy="50" r="4" fill="#E9A63C" opacity="0.35" />
      {/* feet */}
      <path d="M42 92l-4 6M42 92l0 7M42 92l4 6M58 92l-4 6M58 92l0 7M58 92l4 6" stroke="#E9A63C" strokeWidth="2.4" strokeLinecap="round" fill="none" />
    </Box>
  )
}

/** A soft dashed-egg outline — the placeholder for a product with no image. */
export function EggOutline({ sx }: { sx?: SxProps<Theme> }) {
  return (
    <Box component="svg" viewBox="0 0 100 120" width={52} height={62} role="img" aria-hidden sx={sx}>
      <path
        d="M50 8c18 0 34 26 34 52a34 34 0 0 1-68 0C16 34 32 8 50 8z"
        fill="none"
        stroke="rgba(59,42,30,0.3)"
        strokeWidth="3"
        strokeDasharray="5 6"
        strokeLinecap="round"
      />
    </Box>
  )
}

/** The dashed egg-carton divider used between blocks (e.g. inside an order card). */
export function PerforatedDivider({ sx }: { sx?: SxProps<Theme> }) {
  return (
    <Box
      sx={{
        height: '3px',
        backgroundImage: 'radial-gradient(circle, rgba(59,42,30,0.28) 1.5px, transparent 1.7px)',
        backgroundSize: '11px 11px',
        backgroundRepeat: 'repeat-x',
        backgroundPosition: 'center',
        ...sx,
      }}
    />
  )
}

/** A soft polka-dot texture (as an sx `backgroundImage` value) for the terracotta hero panel. */
export const dotTexture =
  'radial-gradient(rgba(255,255,255,0.14) 1.8px, transparent 1.9px)'
